import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { withUrlValidation } from '../components/withUrlValidation';
import { useSessionNavigation } from '../hooks/useSessionNavigation';
import { soundManager } from '../utils/soundUtils';
import BluePopup from '../components/BluePopup';
import { validateUser } from '../utils/userValidator';
import { trackConstellationQREntry } from '../utils/funnelDataUtils';
// @ts-ignore
import fitty from 'fitty';
import topBlur from '../assets/images/12party-topblur.png';
import bottomBlur from '../assets/images/12party-bottomblur.png';
import aniBg from '../assets/images/12party-ani-bg.png';
import finalBg from '../assets/images/12party-bg1.png';
import titleImage from '../assets/images/12party-title.png';
import infoPopImage from '../assets/images/12party-info-pop.png';
import emptyBtnImage from '../assets/images/12party-empty.png';
import inputBgImage from '../assets/images/12party-inputBG.png';
import taleInputImage from '../assets/images/05tale-input.png';
import questionImage from '../assets/images/02register-question.png';
import tipImage from '../assets/images/12party-tip.png';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  overflow: hidden;
  font-family: 'MapoDPP', sans-serif;
`;

const BlackOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  z-index: 10;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.5s ease;
`;

const TopBlurImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  z-index: 10;
`;

const BottomBlurImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: auto;
  z-index: 10;
`;

const AnimatedBackground = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'animate'
})<{ animate: boolean }>`
  position: absolute;
  top: ${({ animate }) => animate ? '-25%' : '0%'};
  right: ${({ animate }) => animate ? '-25%' : '0%'};
  width: 150%;
  height: auto;
  z-index: 5;
  transition: ${({ animate }) => animate ? 'right 1.7s ease-in-out, top 1.7s ease-in-out' : 'none'};
`;

const FinalBackground = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${finalBg}) center/cover no-repeat;
  z-index: 1;
  opacity: ${({ visible }) => visible};
  transition: opacity 1.7s ease;
`;

const TitleImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 7%;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: auto;
  z-index: 20;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
`;

const InfoPopImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
  z-index: 20;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
`;

const InputBackgroundContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
  z-index: 20;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
`;

const InputBackground = styled.img`
  width: 100%;
  height: auto;
`;

const TipButton = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 26%;
  right: 20%;
  width: 4%;
  height: auto;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  pointer-events: none;
  z-index: 22;
`;

const TipButtonOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 26%;
  right: 20%;
  width: 4%;
  height: auto;
  aspect-ratio: 1/1;
  z-index: 22;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
  cursor: pointer;
`;

const TipPopupOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 200;
  opacity: ${({ visible }) => visible};
  transition: opacity 0.3s ease;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoPopupOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 15;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
  background: transparent;
`;

const TipPopupImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 31;
  opacity: ${({ visible }) => visible};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const InputContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 48%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
  z-index: 25;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6vh;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
`;

const InputWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: relative;
  width: 72%;
  aspect-ratio: 4.5/1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
`;

const InputImage = styled.img`
  width: 100%;
  object-fit: cover;
`;

const InputField = styled.input`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  height: 60%;
  background: transparent;
  border: none;
  outline: none;
  font-family: 'Noto Sans KR', 'MapoDPP', 'Nanum Gothic', 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif;
  font-size: 3.5dvw;
  color: #FFFFFF;
  
  &::placeholder {
    color: #999;
  }
`;

const FittedText = ({ children, ...props }: { children: React.ReactNode; minSize?: number; maxSize?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      const fit = fitty(ref.current, { minSize: 12, maxSize: 40, ...props });
      return () => fit.unsubscribe();
    }
  }, [children, props]);
  return <div ref={ref} style={{ width: '100%', height: '100%', textAlign: 'center', fontFamily: 'MapoDPP, sans-serif', color: '#333' }}>{children}</div>;
};

const EnterButton = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 63%;
  left: 50%;
  transform: translateX(-50%);
  width: 80dvw;
  aspect-ratio: 6 / 1; /* 가로:세로 = 5:1 비율 */
  background: url(${emptyBtnImage}) center/cover no-repeat;
  background-size: 100% 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
`;

const ConfirmButton = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
})<{ visible: number }>`
  position: absolute;
  top: 63%;
  left: 50%;
  transform: translateX(-50%);
  width: 80dvw;
  aspect-ratio: 6 / 1;
  background: url(${emptyBtnImage}) center/cover no-repeat;
  background-size: 100% 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  opacity: ${({ visible }) => visible};
  transition: opacity 1s ease;
  pointer-events: ${({ visible }) => visible > 0 ? 'auto' : 'none'};
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
`;

const ButtonText = styled.span`
  color: #FFFFFF;
  font-size: 1.25rem;
  font-family: 'MapoDPP', sans-serif;
  text-shadow: 0 0 10px #64D3FF, 0 0 20px #64D3FF, 0 0 30px #64D3FF;
`;

const ConstellationParty: React.FC = () => {
  const [blackOverlayVisible, setBlackOverlayVisible] = useState(1);
  const [animateBg, setAnimateBg] = useState(false);
  const [finalBgVisible, setFinalBgVisible] = useState(0);
  const [hideAniBg, setHideAniBg] = useState(false);
  const [titleVisible, setTitleVisible] = useState(0);
  const [infoPopVisible, setInfoPopVisible] = useState(0);
  const [enterButtonVisible, setEnterButtonVisible] = useState(0);
  const [inputBgVisible, setInputBgVisible] = useState(0);
  const [inputContainerVisible, setInputContainerVisible] = useState(0);
  const [confirmButtonVisible, setConfirmButtonVisible] = useState(0);
  const [tipButtonVisible, setTipButtonVisible] = useState(0);
  const [tipPopupVisible, setTipPopupVisible] = useState(0);
  const [name, setName] = useState('');
  const [avatarNumber, setavatarNumber] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isClosingPopup, setIsClosingPopup] = useState(false);
  const [pageStartTime] = useState(Date.now());
  const { navigateWithSession } = useSessionNavigation();
  const navigate = useNavigate();

  // 로컬스토리지에서 기존 로그인 정보 가져오기
  useEffect(() => {
    const userInfo = localStorage.getItem('currentUser');
    
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        
        if (parsedUserInfo.nickname) {
          setName(parsedUserInfo.nickname);
        }
        if (parsedUserInfo.avatarNumber) {
          setavatarNumber(parsedUserInfo.avatarNumber);
        }
      } catch (error) {
        console.error('로컬스토리지 데이터 파싱 오류:', error);
      }
    } else {
    }
  }, []);

  // 페이지 시간 추적 (컴포넌트 언마운트 시)
  useEffect(() => {
    return () => {
      const timeSpent = Date.now() - pageStartTime;
    };
  }, [pageStartTime]);

  useEffect(() => {
    
    
    // 퍼널 데이터에 별자리연회 QR 진입 추적
    trackConstellationQREntry();
    
    // 애니메이션 시퀀스 시작
    const sequence = async () => {
      // 1. 검은색에서 페이드아웃 (1초)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setBlackOverlayVisible(0);
      
      // 2. 배경 애니메이션 시작 (0.3초 후)
      await new Promise(resolve => setTimeout(resolve, 300));
      setAnimateBg(true);
      
      // 3. 애니메이션 완료 후 검은색 페이드인 (1.5초 후)
      await new Promise(resolve => setTimeout(resolve, 1500));
      setBlackOverlayVisible(1);
      
      // 4. 최종 배경 페이드인 (1초 후)
      await new Promise(resolve => setTimeout(resolve, 700));
      setFinalBgVisible(1);
      setHideAniBg(true);
      
      // 5. 검은색 페이드아웃 (2초 후)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setBlackOverlayVisible(0);
      
      // 6. 타이틀 페이드인 (1초 후)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTitleVisible(1);
      
      // 7. 정보 팝업 페이드인 (1초 후)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setInfoPopVisible(1);
      
      // 8. 입장하기 버튼 페이드인 (1초 후)
      await new Promise(resolve => setTimeout(resolve, 1000));
      setEnterButtonVisible(1);
    };

    sequence();
  }, []);

  const handleEnterClick = async () => {
    // 입장하기 버튼 클릭 시 애니메이션 시퀀스
    const enterSequence = async () => {
      // 1. infoPopImage와 입장하기 버튼 페이드아웃
      setInfoPopVisible(0);
      setEnterButtonVisible(0);
      
      // 2. 1초 후 inputBG 페이드인
      await new Promise(resolve => setTimeout(resolve, 1000));
      setInputBgVisible(1);
      setTipButtonVisible(1);
      
      // 3. 0.5초 후 inputContainer 페이드인
      await new Promise(resolve => setTimeout(resolve, 500));
      setInputContainerVisible(1);
      
      // 4. 1초 후 확인 버튼 페이드인
      await new Promise(resolve => setTimeout(resolve, 1000));
      setConfirmButtonVisible(1);
      
      // 5. 0.5초 후 자동으로 팁 팝업 표시
      await new Promise(resolve => setTimeout(resolve, 500));
      setTipPopupVisible(1);
    };
    
    enterSequence();
  };

  const handleConfirmClick = async () => {
    // 확인 버튼 클릭 시 처리
    
    // 입력값 검증
    if (!name.trim() || !avatarNumber.trim()) {
      setShowErrorPopup(true);
      return;
    }
    
    try {
      // Firebase에서 사용자 유효성 검사
      const validationResult = await validateUser(name.trim(), avatarNumber.trim());
      
      if (validationResult.isValid) {
        // 유효한 사용자인 경우 로컬 스토리지에 사용자 정보 저장
        const userDataToStore = {
          nickname: name.trim(),
          avatarNumber: avatarNumber.trim(),
          coinCount: 0, // 기본값 설정
          createdAt: validationResult.userData?.createdAt?.toDate?.() ? 
            validationResult.userData.createdAt.toDate().toISOString() : 
            new Date().toISOString(), // Firebase에서 가져온 createdAt 또는 현재 시간
          zoneClearStatus: {
            zone1: '아직안품',
            zone2: '아직안품',
            zone3: '아직안품',
            zone4: '아직안품',
            zone5: '아직안품',
            zone6: '아직안품'
          }
        };
        
        // 로그인 상태와 사용자 정보를 로컬 스토리지에 저장
         console.log('ConstellationParty: 사용자 정보 저장 중', userDataToStore);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('currentUser', JSON.stringify(userDataToStore));
        
        // MessageReceiver가 사용자 정보 변화를 감지할 수 있도록 storage 이벤트 발생
        console.log('ConstellationParty: storage 이벤트 발생');
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'currentUser',
          newValue: JSON.stringify(userDataToStore),
          oldValue: null,
          storageArea: localStorage
        }));
        
        // 추가: MessageReceiver 강제 재시작을 위한 커스텀 이벤트 발생
        console.log('ConstellationParty: MessageReceiver 재시작 이벤트 발생');
        window.dispatchEvent(new CustomEvent('messageReceiverRestart', {
          detail: { nickname: userDataToStore.nickname, avatarNumber: userDataToStore.avatarNumber }
        }));
        
        // 추가: localStorage 직접 변경 이벤트 발생 (새 창에서 더 확실하게 감지)
        setTimeout(() => {
          console.log('ConstellationParty: localStorage 직접 변경 이벤트 발생');
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'currentUser',
            newValue: JSON.stringify(userDataToStore),
            oldValue: null,
            storageArea: localStorage
          }));
        }, 100);
        
        // 프리쇼에서 별자리연회로의 전환 추적
        
        // 메시지 전송 페이지로 이동
        navigate('/message-send');
      } else {
        // 유효하지 않은 사용자인 경우 에러 팝업 표시
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('사용자 검증 중 오류 발생:', error);
      setShowErrorPopup(true);
    }
  };

  const handlePopupClose = () => {
    soundManager.playButtonClick();
    setIsClosingPopup(true);
    setTimeout(() => {
      setShowErrorPopup(false);
      setIsClosingPopup(false);
    }, 1200);
  };

  const handleTipButtonClick = () => {
    setTipPopupVisible(1);
  };

  const handleTipPopupClose = () => {
    setTipPopupVisible(0);
  };

  return (
    <Container>
      {/* 검은색 오버레이 */}
      <BlackOverlay visible={blackOverlayVisible} />
      
      {/* 최종 배경 */}
      <FinalBackground visible={finalBgVisible} />
      
      {/* 상단 블러 이미지 */}
      <TopBlurImage src={topBlur} alt="상단 블러" />
      
      {/* 하단 블러 이미지 */}
      <BottomBlurImage src={bottomBlur} alt="하단 블러" />
      
      {/* 애니메이션 배경 */}
      {!hideAniBg && (
        <AnimatedBackground 
          src={aniBg} 
          alt="애니메이션 배경" 
          animate={animateBg}
        />
      )}
      
      {/* 타이틀 이미지 */}
      <TitleImage 
        src={titleImage} 
        alt="파티 타이틀" 
        visible={titleVisible}
      />
      
      {/* 정보 팝업 오버레이 */}
      <InfoPopupOverlay visible={infoPopVisible} />
      
      {/* 정보 팝업 이미지 */}
      <InfoPopImage 
        src={infoPopImage} 
        alt="파티 정보" 
        visible={infoPopVisible}
      />
      
             {/* 입력 배경 이미지 */}
       <InputBackgroundContainer visible={inputBgVisible}>
         <InputBackground 
           src={inputBgImage} 
           alt="입력 배경" 
         />
         
         {/* 팁 버튼 */}
         <TipButton 
           src={questionImage} 
           alt="팁 버튼" 
           visible={tipButtonVisible}
         />
         <TipButtonOverlay 
           visible={tipButtonVisible}
           onClick={handleTipButtonClick}
         />
       </InputBackgroundContainer>
      
      {/* 입력 컨테이너 */}
      <InputContainer visible={inputContainerVisible}>
        {/* 이름 입력 */}
        <InputWrapper visible={inputContainerVisible}>
          <InputImage src={taleInputImage} alt="이름 입력" />
          <InputField
            type="text"
            placeholder="이름을 입력하세요"
            value={name}
            onChange={(e) => {
              const value = e.target.value;
              // 천지인 키보드 호환성을 위한 개선된 정규식
              // 조합 중인 문자와 모든 한글 모음, 자음을 포함
              const allowedPattern = /^[가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9\s\u3131-\u318E\u1100-\u11FF\uAC00-\uD7AF]*$/;
              
              // 10자 제한과 허용된 문자만 입력 가능하도록 제한
              if (value.length <= 10) {
                // 천지인 키보드의 조합 중인 문자를 포함한 모든 한글 입력 허용
                if (allowedPattern.test(value) || value === '') {
                  setName(value);
                }
              }
            }}
            maxLength={10}
            // 천지인 키보드 호환성을 위한 추가 속성
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </InputWrapper>
        
        {/* 전화번호 입력 */}
        <InputWrapper visible={inputContainerVisible}>
          <InputImage src={taleInputImage} alt="전화번호 입력" />
          <InputField
            type="tel"
            placeholder="화신번호를 입력하세요"
            value={avatarNumber}
            onChange={(e) => setavatarNumber(e.target.value)}
            maxLength={15}
          />
        </InputWrapper>
      </InputContainer>
      
      {/* 입장하기 버튼 */}
      <EnterButton 
        visible={enterButtonVisible}
        onClick={handleEnterClick}
      >
        <ButtonText>입장하기</ButtonText>
      </EnterButton>
      
      {/* 확인 버튼 */}
      <ConfirmButton 
        visible={confirmButtonVisible}
        onClick={handleConfirmClick}
      >
        <ButtonText>확인</ButtonText>
      </ConfirmButton>
      
      {/* 팁 팝업 */}
      <TipPopupOverlay 
        visible={tipPopupVisible}
        onClick={handleTipPopupClose}
      >
        <TipPopupImage 
          src={tipImage} 
          alt="팁" 
          visible={tipPopupVisible}
        />
      </TipPopupOverlay>
      
      {/* 에러 팝업 */}
      {showErrorPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handlePopupClose}
        >
          <BluePopup
            text="[등록되지 않은 화신입니다.]"
            isClosing={isClosingPopup}
            onClose={handlePopupClose}
            fontSize="5dvw"
          />
        </div>
      )}
    </Container>
  );
};

export default withUrlValidation(ConstellationParty, {
  requireValidation: false, // URL 검증 건너뛰기
  showCountdown: false
});
